/**
 * Заметки выбранного поля на карте: круг с хвостом и превью первой фотографии.
 *
 * КРУГ С ХВОСТОМ ВМЕСТО ФЛАЖКА (notes-v4, слова владельца: «стрелочка с кругом, в котором
 * фото первое будет»). Три довода, почему форма правильная, а не просто привычная:
 *
 *   1. КРУГ — ЕДИНСТВЕННАЯ ФОРМА, В КОТОРОЙ МИНИАТЮРА ОСТАЁТСЯ МИНИАТЮРОЙ. Прежняя рамка
 *      28×21 при `object-fit: cover` показывала горизонтальную полоску кадра; на снимке
 *      стерни это полоска бурого. Круг берёт центр кадра — то место, куда человек и целился.
 *   2. ХВОСТ ВОЗВРАЩАЕТ ТОЧНОСТЬ, КОТОРУЮ КРУГ ТЕРЯЕТ. У флажка координатой была пятка
 *      древка; у голого круга — центр, а центр закрыт картинкой, и попасть в него глазом
 *      нельзя. Остриё хвоста — это пятка, вернувшаяся на место.
 *   3. ФОРМА ПО-ПРЕЖНЕМУ РАЗЛИЧАЕТ ТРИ ЗНАКА КАРТЫ. На карте живут ещё два, и оба круглые:
 *      круг с перекрестьем — куда привёл поиск, залитый круг — где сломана геометрия
 *      (`map/markers.ts`, оба глухие). Наш отличается ХВОСТОМ, а не тоном: круг без хвоста
 *      никогда не наш, круг с хвостом всегда наш. Та же защита «форма, не тон», что держала
 *      флажок, — различитель переехал с древка на хвост.
 *
 * ПОПАПА ЗДЕСЬ БОЛЬШЕ НЕТ, И ЭТО ГЛАВНОЕ УДАЛЕНИЕ ВОЛНЫ. Полтысячи строк императивного DOM
 * были ВТОРЫМ редактором той же заметки — со своей датой, своей текстареей, своим вопросом об
 * удалении и своим пультом. Клик по маркеру теперь открывает модалку, и редактор у заметки
 * один. Вместе с попапом ушло перетаскивание: оно жило в нём кнопкой «Подвинуть» и боролось с
 * Leaflet тремя длинными оговорками (`MarkerDrag._onDragStart` закрывает попап ДО
 * `dragstart`, снэпбэк с позиции `dragstart`, Escape на `document`, потому что попапа в этот
 * момент уже нет). Место переставляют тем же `place`-режимом, которым его указывают впервые:
 * один механизм вместо двух, и на телефоне он точнее пальца.
 *
 * `L.divIcon({html})` — innerHTML-сток, и запрет остаётся в силе: ни текст заметки, ни
 * blob-URL миниатюры в разметку не подставляются никогда. Иконка отдаёт только класс и
 * габариты, а тело маркера собирается УЗЛАМИ после `addTo`.
 */

import * as L from "leaflet";

import type { FieldMarker, FieldPhoto } from "../api/types";
import { formatFull } from "../lib/format";
import { T } from "../strings";
import { FIELD_LABEL_MIN_ZOOM } from "./labels";
import { PANES } from "./panes";
import { glyph } from "../components/DrawTools";
import "../components/FieldNotes.css";

/**
 * Тот же пол, что у имён полей, и по той же причине: ниже него поле меньше 4 px поперёк, и
 * знак на нём указывает на то, по чему нельзя попасть.
 *
 * Заметки НЕ прореживаются при наложении. Имя, потерявшее место, читается в списке рядом с
 * картой; выброшенный маркер уносит с собой единственный способ открыть свою заметку, а
 * двадцать заметок в одном углу поставлены нарочно.
 */
export const PIN_MIN_ZOOM = FIELD_LABEL_MIN_ZOOM;

/**
 * С этого масштаба заметка с фотографиями показывает превью, а не точку.
 *
 * Значение и довод прежние (это бывший `PHOTO_THUMB_MIN_ZOOM`), и от смены формы довод только
 * окреп: диск 36 px на зуме 12 — знак шириной в треть поля, то есть знак, закрывающий то,
 * ради чего на него смотрят. Круг закрывает даже больше площади, чем прежняя рамка.
 */
export const PHOTO_PIN_MIN_ZOOM = 14;

/** Сколько держится вспышка «Показать на карте» и ответ на удачную постановку места. */
export const PIN_FLASH_MS = 2000;

/**
 * Что вообще рисуется на этом масштабе. Чистая функция — правило проверяется без карты.
 *
 * Заметка без места на карте не существует: рисовать её негде — координат нет, — а поставить
 * «куда-нибудь» значит соврать про место, которого никто не указывал. Пара проверяется
 * целиком: половины координаты не бывает.
 */
export const visiblePins = (markers: FieldMarker[], zoom: number): FieldMarker[] =>
  zoom < PIN_MIN_ZOOM ? [] : markers.filter((m) => m.lon !== null && m.lat !== null);

/**
 * Три облика одной заметки.
 *
 * `dot` — заметок без фотографий и всё, что ниже порога превью: диск 26 px с флажком внутри.
 * `photo` — диск 36 px с кадром. `stack` — тот же диск и «второй лист» позади: у заметки
 * больше одной фотографии.
 *
 * ЧИСЛОВОГО БЕЙДЖА НЕТ, и это перенос прежнего решения: «3» на знаке 40 px читается как
 * номер, а не как счёт, и на поле с двадцатью заметками превращает карту в таблицу. Сколько
 * именно кадров — сказано в карточке и в модалке, где цифра стоит среди слов.
 */
export type PinShape = "dot" | "photo" | "stack";

export const pinShape = (photos: number, zoom: number): PinShape => {
  if (photos === 0 || zoom < PHOTO_PIN_MIN_ZOOM) return "dot";
  return photos >= 2 ? "stack" : "photo";
};

/**
 * Габарит и якорь каждого облика.
 *
 * ЯКОРЬ — ОСТРИЁ ХВОСТА, и между `photo` и `stack` он НЕ СДВИГАЕТСЯ НИ НА ПИКСЕЛЬ: «второй
 * лист» растёт в слак бокса, а не за его счёт. Это то же правило, что держало пятку древка:
 * знак, у которого от прихода второй фотографии уехала точка, читается как знак, который
 * переехал. Между `dot` и `photo` бокс меняется, но остриё остаётся остриём, и Leaflet ставит
 * на координату именно его.
 */
const PIN_BOX: Record<PinShape, { size: [number, number]; anchor: [number, number] }> = {
  dot: { size: [30, 38], anchor: [15, 38] },
  photo: { size: [44, 54], anchor: [22, 54] },
  stack: { size: [44, 54], anchor: [22, 54] },
};

const PIN_CLASS: Record<PinShape, string> = {
  dot: "k-pin k-pin--dot",
  photo: "k-pin k-pin--photo",
  stack: "k-pin k-pin--photo k-pin--stack",
};

export const pinIcon = (shape: PinShape = "dot", open = false): L.DivIcon => {
  const box = PIN_BOX[shape];
  return L.divIcon({
    className: open ? `${PIN_CLASS[shape]} k-pin--open` : PIN_CLASS[shape],
    iconSize: box.size,
    iconAnchor: box.anchor,
  });
};

/**
 * Доступное имя заметки: дата, текст и счёт фотографий.
 *
 * Дата — `happened_on`: одна заметка обязана звучать одинаково на карте и в списке. Пустой
 * текст — обычное дело, заметку мог завести GPS фотографии.
 */
export const noteAria = (marker: FieldMarker, photos: number): string =>
  T.markers.aria(formatFull(marker.happened_on), marker.note.trim() || null, photos);

/** Чем кончилась серверная правка заметки. Общий тип исхода для карты и для панели. */
export interface PinOutcome {
  ok: boolean;
  message?: string | null;
  /** Id только что созданной заметки — его ждёт модалка создания, чтобы отправить файлы. */
  markerId?: number;
}

/** Глиф флажка ВНУТРИ диска — узлами, а не разметкой: `innerHTML` здесь под запретом. */
const flagNode = (size: number, stroke: number): SVGSVGElement => {
  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", String(size));
  svg.setAttribute("height", String(size));
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", String(stroke));
  svg.setAttribute("stroke-linecap", "butt");
  svg.setAttribute("aria-hidden", "true");
  const path = document.createElementNS(ns, "path");
  path.setAttribute("d", glyph.flag);
  svg.append(path);
  return svg;
};

/**
 * Маркер точки, которую тыкнули на карту, но сервер ещё не принял.
 *
 * НАСТОЯЩИЙ С ПЕРВОЙ СЕКУНДЫ И БЕЗ ПОПАПА. Точка может лежать за контуром, и насколько
 * далеко — знает только сервер; клик обязан приземлиться видимо ДО ответа, иначе он читается
 * как промах. Спрашивать здесь нечего: слова у заметки берёт модалка, которая открывается тем
 * же жестом. Отказ снимает этот маркер, а режим остаётся взведённым — следующий тык это
 * вторая попытка, а не новое решение её сделать.
 */
export const draftPin = (lat: number, lon: number): L.Marker =>
  L.marker([lat, lon], {
    pane: PANES.notes,
    icon: pinIcon("dot", true),
    keyboard: false,
    // Глухой: он ничего не открывает и ни на что не нажимается — он только показывает, куда
    // попал палец, пока летит запрос.
    interactive: false,
  });

export interface PinLayerOptions {
  /** Фотографии ВСЕГО поля: слой сам разложит их по заметкам через `marker_id`. */
  photos?: FieldPhoto[];
  /** Откуда брать миниатюру. Слой кэширует результат и отзывает URL в `detach`. */
  thumbUrl?: (photoId: number) => Promise<string>;
  /**
   * Клик или Enter по маркеру. ЕДИНСТВЕННОЕ, что слой теперь делает по нажатию: открыть
   * модалку этой заметки. Ни попапа, ни своей формы у карты больше нет.
   */
  onOpenNote?: (markerId: number) => void;
}

export interface PinLayer {
  detach: () => void;
  /**
   * Новые заметки и новые фотографии. МЕТОД, А НЕ ПОВТОРНЫЙ `attachPins`: второй вызов — это
   * второй слой. Сюда же приходит каждая загруженная фотография, и превью обязано её догнать.
   */
  update: (markers: FieldMarker[], photos: FieldPhoto[]) => void;
  /**
   * Ненадолго пометить заметки — ответ «Показать на карте» и удачной постановки места.
   *
   * СПИСОК, А НЕ ОДНА, потому что спрашивают двумя разными вопросами: «где вот эта» (одна) и
   * «а где они все» (все). Без пометки человек оказывается перед снимком со спутника, на
   * котором где-то стоит знак 40 px: «показали на карте» без указания — это «показали карту».
   */
  highlight: (markerIds: number[]) => void;
  /** Какая заметка сейчас открыта в модалке: её маркер помечен тоном. `null` — ни одной. */
  setOpen: (markerId: number | null) => void;
}

/** Один слой на одну заметку: сам маркер и его нынешний облик. */
interface Entry {
  layer: L.Marker;
  marker: FieldMarker;
  shape: PinShape;
}

/**
 * Ставит заметки одного поля на карту и держит их в согласии с данными и масштабом.
 *
 * СВЕРКА ПО id, А НЕ ПЕРЕСБОРКА. Любой новый массив `markers` уничтожал бы все слои и строил
 * заново — а каждая загруженная фотография возвращается новым массивом, то есть маркер
 * пересобирался бы в ответ на действие, совершённое над ним же: терялись бы наведение,
 * фокус и пометка «открыта». Поэтому живые слои сохраняются, а меняются у них только
 * позиция, иконка и доступное имя. Пороги масштаба — тоже смена иконки, а не пересборка.
 */
export const attachPins = (
  map: L.Map,
  markers: FieldMarker[],
  options: PinLayerOptions,
): PinLayer => {
  const entries = new Map<number, Entry>();
  let lit = new Set<number>();
  let flashTimer = 0;
  let current = markers;
  let album: FieldPhoto[] = options.photos ?? [];
  let openId: number | null = null;

  /**
   * ОДИН КЭШ БЛОБОВ НА ВЕСЬ СЛОЙ. Каждый blob-URL держит в памяти декодированную картинку,
   * пока его не отозвали; два кэша значили бы две копии одного кадра и два отзыва, из которых
   * второй некому сделать. Отзыв ровно один — в `detach`.
   */
  const blobs = new Map<number, string>();
  const missing = new Set<number>();
  /**
   * КАДРЫ, КОТОРЫЕ ЕДУТ ПРЯМО СЕЙЧАС, и это не оптимизация, а лечение пустого диска.
   *
   * `update` перерисовывает тело, пока в диске нет картинки, а перерисовка сносит узел вместе
   * с незавершённой загрузкой (`disc.isConnected` становится ложью, и приехавший кадр
   * выбрасывается). Родитель же перерисовывается часто — прогресс прогона, очередь загрузки,
   * вспышки, — и на медленной связи маркер оставался кругом без кадра сколько угодно долго,
   * начиная загрузку заново с каждым рендером. Тот же класс дефекта, что «жив ли просмотр» в
   * модалке: живое состояние нельзя привязывать к узлу, который перерисовка убивает.
   */
  const loading = new Set<number>();
  /** Слой снят. Кадры, приехавшие после этого, отзываются на месте — класть их некуда. */
  let detached = false;

  const thumbUrl = async (photoId: number): Promise<string> => {
    const held = blobs.get(photoId);
    if (held !== undefined) return held;
    const load = options.thumbUrl;
    if (!load) throw new Error("pins: thumbnails were not offered to this layer");
    const url = await load(photoId);
    if (detached) {
      URL.revokeObjectURL(url);
      return url;
    }
    // Гонка двух запросов на один кадр: победителя оставляем, проигравшего отзываем сразу,
    // иначе он повиснет в памяти до конца жизни документа без единой ссылки на себя.
    const won = blobs.get(photoId);
    if (won !== undefined) {
      URL.revokeObjectURL(url);
      return won;
    }
    blobs.set(photoId, url);
    return url;
  };

  /** Фотографии ОДНОЙ заметки, в том же порядке, в каком их отдал вызывающий. */
  const photosOf = (markerId: number): FieldPhoto[] =>
    album.filter((photo) => photo.marker_id === markerId);

  /**
   * Тело маркера — УЗЛАМИ: хвост, «второй лист» (только у стопки) и диск, а в диск — кадр
   * или флажок. Пересобирается при смене облика и достраивается, когда кадр доехал.
   *
   * ДЕГРАДАЦИЯ БЕЗ ПРЕВЬЮ ВАЖНЕЕ, ЧЕМ КАЖЕТСЯ. Кадр может не прийти (сеть, 404, отозванный
   * токен). Диск НЕ схлопывается в точку: знак, съёжившийся с 36 до 26 px, читается как знак,
   * который переехал, — а он стоит на том же месте. Он остаётся кругом с флажком внутри, и
   * стопка остаётся стопкой: сколько кадров, мы знаем из списка, даже не зная, как они
   * выглядят.
   */
  const paintBody = (entry: Entry): void => {
    const element = entry.layer.getElement();
    if (!element) return;
    element.replaceChildren();

    const tail = document.createElement("span");
    tail.className = "k-pin__tail";
    element.append(tail);

    if (entry.shape === "stack") {
      const sheet = document.createElement("span");
      sheet.className = "k-pin__sheet";
      element.append(sheet);
    }

    const disc = document.createElement("span");
    disc.className = "k-pin__disc";
    element.append(disc);

    if (entry.shape === "dot") {
      disc.append(flagNode(14, 1.5));
      return;
    }

    const first = photosOf(entry.marker.id)[0];
    if (!first || !options.thumbUrl || missing.has(first.id)) {
      element.classList.add("k-pin--blind");
      disc.append(flagNode(16, 1.5));
      return;
    }
    loading.add(first.id);
    void thumbUrl(first.id).then(
      (url) => {
        loading.delete(first.id);
        // Облик мог смениться (ушли фотографии, уехал зум), и тогда этот узел уже не в
        // документе: вешать на него картинку значит держать блоб ни для кого.
        if (!disc.isConnected) return;
        const image = document.createElement("img");
        image.src = url;
        image.alt = "";
        image.decoding = "async";
        disc.append(image);
      },
      () => {
        // Кадр не пришёл — диск остаётся диском, ГАБАРИТ НЕ МЕНЯЕТСЯ.
        loading.delete(first.id);
        missing.add(first.id);
        if (!disc.isConnected) return;
        element.classList.add("k-pin--blind");
        disc.append(flagNode(16, 1.5));
      },
    );
  };

  /** Вспышка и пометка «открыта»: классы на живом узле, без пересборки слоя. */
  const paintState = (): void => {
    for (const [id, entry] of entries) {
      const element = entry.layer.getElement();
      element?.classList.toggle("k-pin--flash", lit.has(id));
      element?.classList.toggle("k-pin--open", openId === id);
    }
  };

  /**
   * ПОДСКАЗКА ПРИ НАВЕДЕНИИ — И НИ ОДНОЙ КНОПКИ В НЕЙ. Это не третий редактор и не
   * мини-попап: это подпись, позволяющая просмотреть двадцать маркеров мышью, не открыв ни
   * одной модалки. На касании её нет, и не надо: пальцем маркер открывается одним тычком.
   */
  const tooltipFor = (marker: FieldMarker): string =>
    // `noteLabel` брать нельзя: он возвращает строку, а тултип Leaflet принимает её как HTML.
    // Текст заметки — пользовательский ввод, поэтому он уходит отдельным текстовым узлом.
    T.markers.peek(formatFull(marker.happened_on), marker.note.trim() || T.markers.noText);

  const bindTooltip = (entry: Entry): void => {
    const node = document.createElement("span");
    node.textContent = tooltipFor(entry.marker);
    entry.layer.bindTooltip(node, { direction: "top", offset: [0, -8], opacity: 1 });
  };

  const create = (marker: FieldMarker): Entry => {
    const shape = pinShape(photosOf(marker.id).length, map.getZoom());
    // Место заведомо есть: `visiblePins` отсеяла заметки без пары ещё до `sync`.
    const layer = L.marker([marker.lat ?? 0, marker.lon ?? 0], {
      pane: PANES.notes,
      icon: pinIcon(shape, false),
      keyboard: true,
    });
    const entry: Entry = { layer, marker, shape };

    // ЕДИНСТВЕННОЕ ДЕЙСТВИЕ МАРКЕРА. Попапа нет, перетаскивания нет — клик открывает модалку.
    layer.on("click", () => options.onOpenNote?.(entry.marker.id));
    layer.on("keypress", (event: L.LeafletKeyboardEvent) => {
      if (event.originalEvent.key === "Enter" || event.originalEvent.key === " ") {
        options.onOpenNote?.(entry.marker.id);
      }
    });
    // НАВЕРХ при наведении: заметки не прореживаются и законно стоят внахлёст.
    layer.on("mouseover", () => layer.setZIndexOffset(1000));
    layer.on("mouseout", () => layer.setZIndexOffset(openId === entry.marker.id ? 1000 : 0));

    layer.addTo(map);
    bindTooltip(entry);
    // `keyboard: true` даёт узлу `tabindex="0"` и `role="button"`, поэтому Tab доходит до
    // заметки. Доступное имя ставится на ТОТ ЖЕ узел — фокусируемый — и потому после `addTo`.
    layer.getElement()?.setAttribute("aria-label", noteAria(marker, photosOf(marker.id).length));
    paintBody(entry);
    return entry;
  };

  /** Обновить живой слой под новые данные. Всё, кроме `setIcon`, — правка на месте. */
  const update = (entry: Entry, marker: FieldMarker): void => {
    const was = entry.marker;
    entry.marker = marker;
    const count = photosOf(marker.id).length;
    const at = entry.layer.getLatLng();
    if (
      marker.lat !== null &&
      marker.lon !== null &&
      (at.lat !== marker.lat || at.lng !== marker.lon)
    ) {
      entry.layer.setLatLng([marker.lat, marker.lon]);
    }
    const shape = pinShape(count, map.getZoom());
    if (shape !== entry.shape) {
      entry.shape = shape;
      // Единственное место, где узел заменяется целиком. Поэтому сразу за ним — и тело, и имя,
      // и состояния: всё, что жило на прежнем узле.
      entry.layer.setIcon(pinIcon(shape, openId === marker.id));
      paintBody(entry);
    } else if (shape !== "dot") {
      // Кадр мог прийти позже иконки или смениться первым в ленте — узел тот же, меняется
      // только содержимое диска. НО НЕ ПОКА ОН ЕДЕТ: перерисовка снесла бы узел вместе с
      // незавершённой загрузкой и начала бы её заново, а родитель перерисовывается часто.
      const element = entry.layer.getElement();
      const first = photosOf(marker.id)[0];
      const busy = first !== undefined && loading.has(first.id);
      if (element && !busy && !element.querySelector(".k-pin__disc img")) paintBody(entry);
    }
    entry.layer.getElement()?.setAttribute("aria-label", noteAria(marker, count));
    // Подпись держит дату и текст: правка заметки обязана до неё доезжать.
    if (was.note !== marker.note || was.happened_on !== marker.happened_on) {
      const node = document.createElement("span");
      node.textContent = tooltipFor(marker);
      entry.layer.setTooltipContent(node);
    }
  };

  const drop = (entry: Entry): void => {
    entry.layer.off();
    map.removeLayer(entry.layer);
  };

  /** Свести карту слоёв с тем, что пришло. Ни одного лишнего пересоздания. */
  const sync = (): void => {
    const wanted = visiblePins(current, map.getZoom());
    const seen = new Set<number>();
    for (const marker of wanted) {
      seen.add(marker.id);
      const entry = entries.get(marker.id);
      if (entry) update(entry, marker);
      else entries.set(marker.id, create(marker));
    }
    for (const [id, entry] of entries) {
      if (seen.has(id)) continue;
      drop(entry);
      entries.delete(id);
    }
    paintState();
  };

  sync();
  // Пересчёт на каждом `zoomend`, но пересборки в нём нет: оба порога меняют только иконку, а
  // `sync` меняет её лишь тогда, когда облик действительно стал другим.
  const onZoomEnd = (): void => sync();
  map.on("zoomend", onZoomEnd);

  return {
    detach: () => {
      detached = true;
      window.clearTimeout(flashTimer);
      map.off("zoomend", onZoomEnd);
      for (const entry of entries.values()) drop(entry);
      entries.clear();
      // Единственный отзыв блобов на весь слой. Без него каждое открытие поля оставляло бы в
      // памяти по декодированной картинке на заметку до конца жизни документа.
      for (const url of blobs.values()) URL.revokeObjectURL(url);
      blobs.clear();
    },
    highlight: (markerIds: number[]) => {
      window.clearTimeout(flashTimer);
      lit = new Set(markerIds);
      paintState();
      flashTimer = window.setTimeout(() => {
        lit = new Set();
        paintState();
      }, PIN_FLASH_MS);
    },
    setOpen: (markerId: number | null) => {
      openId = markerId;
      for (const [id, entry] of entries) {
        entry.layer.setZIndexOffset(markerId === id ? 1000 : 0);
      }
      paintState();
    },
    update: (nextMarkers: FieldMarker[], nextPhotos: FieldPhoto[]) => {
      current = nextMarkers;
      album = nextPhotos;
      sync();
    },
  };
};
